import React, { Fragment, useRef } from 'react';
import CustomToolTip from '../../Common/CustomToolTip';
import { extract_TLE } from '../../../../../../../utils/tleHelpers';
import styles from './TleInput.module.scss';
import { CloseSvg } from '../../../../../../command_center/components/center/planning/Svgs';

export interface TleInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  onPaste?: (value: string) => void;
  hasError?: boolean;
  tleComplete?: boolean;
  tleError?: boolean | string;
  placeholder?: string;
  rows?: number;

  // File Upload & Bulk state props
  onFileUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  tleEntriesCount?: number;
  tlePreviewLoading?: boolean;

  // Uploaded file info & clear handler
  isUploaded?: boolean;
  uploadedFileName?: string;
  onClearFile?: () => void;
  readOnly?: boolean;
}

export const TleInput: React.FC<TleInputProps> = ({
  value,
  onChange,
  onBlur,
  onPaste,
  hasError,
  tleComplete,
  tleError,
  placeholder = '1 25544U 98067A...\n2 25544  51.641...',
  rows = 4,
  onFileUpload,
  tleEntriesCount,
  tlePreviewLoading,
  isUploaded = false,
  uploadedFileName,
  onClearFile,
  readOnly = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatTleString = (inputValue: string, isTypingChar: boolean, isDeleting: boolean): string => {
    // Strip out any invalid characters (only allow letters, numbers, spaces, dots, dashes, plus, and newlines)
    let val = inputValue.replace(/[^A-Za-z0-9\s.\-+\n]/g, '');

    // If user is deleting/backspacing, allow free deletion without re-adding spaces!
    if (isDeleting) {
      const lines = val.split('\n');
      return lines.map(line => {
        let l = line;
        if (l.length > 69) l = l.slice(0, 69);
        return l;
      }).join('\n');
    }

    // 1. Try reusing extract_TLE helper for pasted/complete multi-line or 138-char TLE inputs
    const extracted = extract_TLE(val);
    if (extracted) {
      return `${extracted.line_1.slice(0, 69)}\n${extracted.line_2.slice(0, 69)}`;
    }

    // Handle continuous 138-character TLE string without newlines (69 chars line 1 + 69 chars line 2)
    if (!val.includes('\n') && val.length >= 138) {
      let l1 = val.substring(0, 69);
      let l2 = val.substring(69, 138);
      if (!l1.startsWith('1')) l1 = '1 ' + l1.replace(/^[12]\s*/, '');
      if (!l2.startsWith('2')) l2 = '2 ' + l2.replace(/^[12]\s*/, '');
      return `${l1.slice(0, 69)}\n${l2.slice(0, 69)}`;
    }

    const lines = val.split('\n');

    // 2. Format lines and enforce strict 69-character max length & line prefixes
    const formattedLines = lines.map((line, index) => {
      let l = line;
      if (!l) return l;

      if (lines.length <= 2) {
        // 2-Line Format: Line 1 must start with '1 ', Line 2 must start with '2 '
        if (index === 0) {
          if (!l.startsWith('1')) {
            l = '1 ' + l.replace(/^[12]\s*/, '');
          } else if (l.length === 1) {
            l = '1 ';
          } else if (l[1] !== ' ') {
            l = '1 ' + l.slice(1);
          }

          const len = l.length;
          if (len === 8 && l[7] !== ' ') l = l + ' ';
          else if (len === 17 && l[16] !== ' ') l = l + ' ';
          else if (len === 32 && l[31] !== ' ') l = l + ' ';
          else if (len === 43 && l[42] !== ' ') l = l + ' ';
          else if (len === 52 && l[51] !== ' ') l = l + ' ';
          else if (len === 61 && l[60] !== ' ') l = l + ' ';
          else if (len === 63 && l[62] !== ' ') l = l + ' ';
        } else if (index === 1) {
          if (!l.startsWith('2')) {
            l = '2 ' + l.replace(/^[12]\s*/, '');
          } else if (l.length === 1) {
            l = '2 ';
          } else if (l[1] !== ' ') {
            l = '2 ' + l.slice(1);
          }

          const len = l.length;
          if (len === 7 && l[6] !== ' ') l = l + ' ';
          else if (len === 16 && l[15] !== ' ') l = l + ' ';
          else if (len === 25 && l[24] !== ' ') l = l + ' ';
          else if (len === 33 && l[32] !== ' ') l = l + ' ';
          else if (len === 42 && l[41] !== ' ') l = l + ' ';
          else if (len === 51 && l[50] !== ' ') l = l + ' ';
          else if (len === 63 && l[62] !== ' ') l = l + ' ';
        }
      } else {
        // 3-Line Format (Name, Line 1, Line 2)
        if (index === 1) {
          if (!l.startsWith('1')) l = '1 ' + l.replace(/^[12]\s*/, '');
          else if (l.length === 1) l = '1 ';
          else if (l[1] !== ' ') l = '1 ' + l.slice(1);
        } else if (index === 2) {
          if (!l.startsWith('2')) l = '2 ' + l.replace(/^[12]\s*/, '');
          else if (l.length === 1) l = '2 ';
          else if (l[1] !== ' ') l = '2 ' + l.slice(1);
        }
      }

      // CRITICAL: Strictly clamp EVERY line unconditionally to a maximum of 69 characters!
      if (l.length > 69) {
        l = l.slice(0, 69);
      }
      return l;
    });

    let finalVal = formattedLines.join('\n');

    if (isTypingChar) {
      const processedLines = finalVal.split('\n');

      // Auto newline after 69 characters if typing line 1
      if (processedLines.length === 1 && processedLines[0].startsWith('1 ') && processedLines[0].length === 69) {
        finalVal = finalVal + '\n2 ';
      }
    }

    return finalVal;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (readOnly || isUploaded) return;

    if (e.key === 'Enter') {
      const textarea = e.currentTarget;
      const val = textarea.value;
      const lines = val.split('\n');

      // If user is typing line 1 and presses Enter
      if (lines.length === 1 && lines[0].startsWith('1')) {
        e.preventDefault();
        const l1 = lines[0].length < 69 ? lines[0].padEnd(69, ' ') : lines[0].slice(0, 69);
        const newVal = l1 + '\n2 ';
        onChange(newVal);
      } else if (lines.length === 2 && lines[0].startsWith('1') && lines[1].startsWith('2') && textarea.selectionStart <= lines[0].length) {
        // If cursor is on line 1, move to line 2
        e.preventDefault();
        const l1 = lines[0].length < 69 ? lines[0].padEnd(69, ' ') : lines[0].slice(0, 69);
        const newVal = l1 + '\n' + lines[1];
        onChange(newVal);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (readOnly || isUploaded) return;
    const rawVal = e.target.value;
    const isTypingChar = rawVal.length === value.length + 1;
    const isDeleting = rawVal.length < value.length;
    const formattedVal = formatTleString(rawVal, isTypingChar, isDeleting);
    onChange(formattedVal);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (readOnly || isUploaded) return;
    const text = e.clipboardData.getData('text');
    if (!text.trim()) return;
    e.preventDefault();
    const formattedVal = formatTleString(text, false, false);
    if (onPaste) {
      onPaste(formattedVal);
    } else {
      onChange(formattedVal);
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur(value);
    }
  };

  const lineCount = value ? value.split('\n').filter(l => l.trim().length > 0).length : 0;
  const isExpanded = lineCount > 3 || (tleEntriesCount !== undefined && tleEntriesCount > 1);
  const isInputReadOnly = readOnly || isUploaded;

  return (
    <div className={styles.tleSection}>
      <div className={styles.tleLabelRow}>
        <span className={styles.tleSectionTitle}>TLE Input</span>
        <CustomToolTip title="2-line TLE format" placement="top">
          <span className={styles.infoIcon}>ⓘ</span>
        </CustomToolTip>
      </div>

      <div className={`${styles.tleTextAreaContainer} ${isInputReadOnly ? styles.containerReadOnly : ''}`}>
        <textarea
          className={`${styles.tleTextArea} ${isExpanded ? styles.tleTextAreaExpanded : ''} ${isInputReadOnly ? styles.tleTextAreaReadOnly : ''} ${hasError ? styles.fieldInputError : ''}`}
          value={value}
          placeholder={placeholder}
          spellCheck={false}
          rows={isExpanded ? 8 : rows}
          readOnly={isInputReadOnly}
          tabIndex={isInputReadOnly ? -1 : 0}
          onFocus={e => {
            if (isInputReadOnly) e.currentTarget.blur();
          }}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
        />
      </div>

      {tleComplete && !tleError && (
        <span className={styles.tleBulkHint}>TLE valid — orbit shown on map.</span>
      )}

      {onFileUpload && (
        <Fragment>
          <div className={styles.orDivider}>OR</div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".tle,.txt,.3le"
            className={styles.tleFileInput}
            onChange={onFileUpload}
          />

          {isUploaded || uploadedFileName ? (
            <div className={styles.uploadedFilePill}>
              <div className={styles.fileNameGroup}>
                <span className={styles.fileIcon}>📄</span>
                <span className={styles.fileName}>{uploadedFileName || 'Uploaded file'}</span>
              </div>
              {onClearFile && (
                <button
                  type="button"
                  className={styles.clearFileBtn}
                  onClick={onClearFile}
                  title="Remove uploaded file"
                >
                  <CloseSvg />
                </button>
              )}
            </div>
          ) : (
            <button
              className={styles.uploadBtn}
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              + UPLOAD .TXT FILE
            </button>
          )}

          {tleEntriesCount !== undefined && tleEntriesCount > 1 && (
            <span className={styles.tleBulkHint}>
              {tleEntriesCount} satellites in file — all shown on map; Create adds each.
            </span>
          )}

          {tlePreviewLoading && (
            <span className={styles.tleBulkHint}>Fetching CelesTrak GP data…</span>
          )}
        </Fragment>
      )}
    </div>
  );
};

export default TleInput;
